import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Layers } from "lucide-react";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import {
  PortfolioInventoryPanel,
  PortfolioOrgUnreadable,
  PortfolioUnavailable,
} from "@/components/organisms/migrations/PortfolioInventoryPanel";
import {
  usePortfolioEstimate,
  usePortfolioInventory,
  usePortfolioPassCreate,
  usePortfolioPassRun,
  usePortfolioPassTriage,
} from "@/hooks/migrations/portfolio";
import {
  countLabel,
  estimateSummary,
  passRequestFrom,
  resolvePortfolioGate,
} from "@/lib/migrations/portfolio";
import {
  isOrgUnreadable,
  isPortfolioAbsent,
  portfolioErrorMessage,
} from "@/services/migrations/portfolio";
import type { PortfolioEstimate, PortfolioPassTriage } from "@/services/migrations/portfolio";

/**
 * Org-wide automation triage: the portfolio pass.
 *
 * WHAT THIS PAGE IS FOR. An org with 370 WorkflowRules does not want 370 reports; it
 * wants to know that they fall into twelve shapes. The pass produces that — but it is a
 * PAID capability, and the deliberate product decision is that the pass stays inert for
 * an org that has not bought it while the PICTURE of the org's scale stays free. So this
 * page always renders the inventory, and the run is the only thing behind the gate.
 *
 * THE GATE IS NOT ENFORCED HERE. All four paid routes check entitlement server-side and
 * refuse with a 403. This page's disabled button prevents a pointless request; it does
 * not prevent an unauthorised one, and nothing about it should be read as though it did.
 * The 403 is handled as a first-class outcome: `resolvePortfolioGate` folds any refusal
 * from any paid call back into the same gate the free inventory produced, so a refused
 * request lands on the identical contact path rather than on an error banner.
 *
 * THE FLOW, once entitled: estimate → confirm → create → run → triage. `accept_estimate`
 * is sent only from the confirm step (`passRequestFrom`), because the field means a human
 * read the estimate and no other code path can honestly claim that.
 */
export function PortfolioPass() {
  const inventoryQ = usePortfolioInventory();
  const estimate = usePortfolioEstimate();
  const create = usePortfolioPassCreate();
  const run = usePortfolioPassRun();

  const [confirming, setConfirming] = useState<PortfolioEstimate | null>(null);
  const [planId, setPlanId] = useState<string | undefined>(undefined);
  const [blocked, setBlocked] = useState<string | null>(null);

  const triageQ = usePortfolioPassTriage(planId);

  // The FIRST refusal wins, in the order the calls happen. Any 403 among them closes the
  // gate — see `resolvePortfolioGate`, which lets the server's answer outrank the
  // inventory's.
  const paidError = estimate.error ?? create.error ?? run.error ?? triageQ.error ?? null;
  const gate = resolvePortfolioGate(inventoryQ.data?.triage ?? null, paidError);

  const busy = estimate.isPending || create.isPending || run.isPending;

  const startEstimate = () => {
    setBlocked(null);
    estimate.mutate(undefined, { onSuccess: (data) => setConfirming(data) });
  };

  const confirmPass = (accepted: PortfolioEstimate) => {
    const request = passRequestFrom(accepted);
    if (!request.ok) {
      setBlocked(request.reason);
      return;
    }
    create.mutate(request.body, {
      onSuccess: (created) => {
        setConfirming(null);
        setPlanId(created.plan_id);
        // Create and run are separate routes on purpose; the pass exists the moment it is
        // created, so a run that fails leaves something a reader can come back to.
        run.mutate(created.plan_id);
      },
    });
  };

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in bg-background">
      <PageHeader
        icon={Layers}
        title="Portfolio pass"
        description="Every WorkflowRule in this org, triaged into the shapes it actually falls into"
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/migration-engine">
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              Migration engine
            </Link>
          </Button>
        }
      />

      <div className="flex-1 min-h-0 space-y-5 overflow-y-auto">
        {inventoryQ.isLoading ? (
          <p className="font-mono text-xs text-muted-foreground">Reading this org's automation…</p>
        ) : inventoryQ.isError ? (
          <InventoryFailure error={inventoryQ.error} />
        ) : inventoryQ.data ? (
          <>
            <PortfolioInventoryPanel
              inventory={inventoryQ.data}
              gate={gate}
              onRun={startEstimate}
              running={busy}
            />

            {blocked ? (
              <p className="max-w-prose font-mono text-xs text-[hsl(var(--accent-warm))]">
                {blocked}
              </p>
            ) : null}

            {/* A non-403 failure of a paid call is a real error and says so. A 403 is
                already rendered as the contact path inside the panel above, so it is not
                repeated here as red text. */}
            {paidError && gate.available ? (
              <p className="max-w-prose font-mono text-xs text-destructive">
                {portfolioErrorMessage(paidError)}
              </p>
            ) : null}

            {confirming ? (
              <EstimateConfirm
                estimate={confirming}
                pending={create.isPending}
                onCancel={() => setConfirming(null)}
                onConfirm={() => confirmPass(confirming)}
              />
            ) : null}

            {planId ? <PassTriageView planId={planId} triage={triageQ.data} /> : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Why the inventory could not be read, in three distinguishable ways.
 *
 * `org_unreadable` is separated out because it is the one that must NOT send a reader to
 * a salesperson: nothing about a 502 is an entitlement problem, and a page that answered
 * it with "contact your account team" would be sending someone to buy a fix for a
 * connection failure.
 */
function InventoryFailure({ error }: { error: unknown }) {
  if (isPortfolioAbsent(error)) return <PortfolioUnavailable />;
  if (isOrgUnreadable(error)) return <PortfolioOrgUnreadable message={portfolioErrorMessage(error)} />;
  return (
    <p className="max-w-prose font-mono text-xs text-destructive">
      This org's inventory could not be read: {portfolioErrorMessage(error)}
    </p>
  );
}

/**
 * The estimate, and the one place `accept_estimate: true` becomes reachable.
 *
 * A confirm step is the last screen before a customer's deploy queue is occupied, which
 * makes it the worst possible place to narrow the numbers to the flattering subset. So it
 * restates `unaddressable` rather than showing only what the pass covers, it leads with
 * `wall_clock_human` — the figure a customer actually feels — and it never lets a
 * duration derived from a DEFAULT per-deploy timing read as one measured on this org.
 */
function EstimateConfirm({
  estimate,
  pending,
  onCancel,
  onConfirm,
}: {
  estimate: PortfolioEstimate;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const summary = estimateSummary(estimate);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-sm text-foreground">Confirm the portfolio pass</p>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        The pass will be built over{" "}
        <span className="font-semibold text-foreground">
          {countLabel(estimate.counts.addressable)}
        </span>{" "}
        of {countLabel(estimate.counts.workflow_rules)} rules.{" "}
        {countLabel(estimate.counts.unaddressable)} cannot be addressed and are not part of
        it. Scope: <span className="font-mono">{estimate.scope || "—"}</span>.
      </p>

      {/* The cost a customer feels is time on their deploy queue, so it leads. */}
      {summary.wallClock ? (
        <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-2xl tabular-nums text-foreground">
            {summary.wallClock}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            estimated time on this org&apos;s deploy queue
          </span>
          <Badge variant={summary.wallClockMeasured ? "secondary" : "warm"}>
            {summary.wallClockMeasured
              ? `timing measured on this org${summary.timingSamples != null ? ` · ${summary.timingSamples} samples` : ""}`
              : "timing not measured on this org"}
          </Badge>
        </div>
      ) : null}

      <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
        {summary.deploys != null ? (
          <div>
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              deploys{summary.deploysUpperBound ? " (at most)" : ""}
            </dt>
            <dd className="font-mono text-sm text-foreground">{summary.deploys}</dd>
          </div>
        ) : null}
        {summary.components != null ? (
          <div>
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              components
            </dt>
            <dd className="font-mono text-sm text-foreground">{summary.components}</dd>
          </div>
        ) : null}
        {summary.apiCalls != null ? (
          <div>
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              api calls
            </dt>
            <dd className="font-mono text-sm text-foreground">{summary.apiCalls}</dd>
          </div>
        ) : null}
        {summary.concurrency != null ? (
          <div>
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              concurrency
            </dt>
            <dd className="font-mono text-sm text-foreground">
              {summary.concurrency}
              {summary.concurrencyReason ? (
                <span className="ml-1.5 text-[11px] text-muted-foreground">
                  {summary.concurrencyReason}
                </span>
              ) : null}
            </dd>
          </div>
        ) : null}
        {/* Anything the server sent that this client does not surface by name. A field
            added server-side shows up here rather than vanishing. */}
        {summary.rows.map((row) => (
          <div key={row.label}>
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              {row.label}
            </dt>
            <dd className="font-mono text-sm text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>

      {summary.caveats.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {summary.caveats.map((caveat) => (
            <li key={caveat} className="max-w-prose text-xs text-[hsl(var(--accent-warm))]">
              {caveat}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" onClick={onConfirm} disabled={pending} aria-disabled={pending}>
          {pending ? "Creating…" : "Accept the estimate and run"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The clustered result. Shapes, with the counts the server reported for each. */
function PassTriageView({
  planId,
  triage,
}: {
  planId: string;
  triage: PortfolioPassTriage | undefined;
}) {
  if (!triage) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        Pass {planId} is running. Waiting for the first triage result…
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <RunStatusBadge status={triage.status} />
        <span className="font-mono text-[11px] text-muted-foreground">
          {triage.clusters.length} shape{triage.clusters.length === 1 ? "" : "s"} · pass{" "}
          {triage.plan_id}
        </span>
      </div>

      {Object.keys(triage.counts).length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {Object.entries(triage.counts).map(([key, value]) => (
            <Badge key={key} variant="secondary">
              {key.replace(/_/g, " ")} {value}
            </Badge>
          ))}
        </div>
      ) : null}

      {triage.clusters.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">
          No shapes have been reported yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {triage.clusters.map((cluster) => (
            <li key={cluster.key} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-sm text-foreground">{cluster.label}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {countLabel(cluster.count)} rule{cluster.count === 1 ? "" : "s"}
                </span>
              </div>
              {cluster.fields.length > 0 ? (
                <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                  {cluster.fields.map((field) => (
                    <div key={field.label} className="flex gap-1.5">
                      <dt className="font-mono text-[11px] text-muted-foreground">
                        {field.label}
                      </dt>
                      <dd className="font-mono text-[11px] text-foreground">{field.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PortfolioPass;
